import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  List,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import { useState } from 'react';

import type { SubFilterI } from 'declarations/interfaces/common';

type InsightsOemSelectPropsT = {
  value: SubFilterI[];
  onChange: (filters: SubFilterI[]) => void;
  onOemChange?: () => void;
};

/** OEM dropdown for Insights – searchable menu with checkboxes, matches Dealer Card. */
export const InsightsOemSelect: FC<InsightsOemSelectPropsT> = ({
  value,
  onChange,
  onOemChange,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOems = value.filter(
    (oem): boolean =>
      !searchQuery.trim() || oem.label.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const handleToggle = (oemValue: string): void => {
    setMenuAnchor(null);
    setSearchQuery('');
    onChange(
      value.map((o): SubFilterI => (o.value === oemValue ? { ...o, checked: !o.checked } : o))
    );
    onOemChange?.();
  };

  const count = value.filter((f): boolean => f.checked).length;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        sx={{
          minWidth: 100,
          justifyContent: 'space-between',
          fontSize: 12,
          height: 28,
          textTransform: 'none',
        }}
        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
        onClick={(e: MouseEvent<HTMLButtonElement>): void => {
          setSearchQuery('');
          setMenuAnchor(e.currentTarget);
        }}
      >
        OEMs
        {count > 0 ? (
          <>
            {' '}
            <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>
              {`(${count})`}
            </Box>
          </>
        ) : null}
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={(): void => {
          setMenuAnchor(null);
          setSearchQuery('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            maxHeight: 380,
            minWidth: 200,
            width: 'max-content',
            maxWidth: 500,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 0,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search OEMs"
            value={searchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => setSearchQuery(e.target.value)}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear OEM search"
                    size="small"
                    onClick={(): void => setSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {filteredOems.map(
            (oem): JSX.Element => (
              <MenuItem key={oem.value} onClick={(): void => handleToggle(oem.value)}>
                <Checkbox checked={oem.checked} disableRipple size="small" />
                <ListItemText primary={oem.label} sx={{ '& span': { whiteSpace: 'nowrap' } }} />
              </MenuItem>
            )
          )}
          {searchQuery.trim() && filteredOems.length === 0 && (
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                px: 2,
                py: 1,
              }}
            >
              No OEMs match your search.
            </Typography>
          )}
        </List>
      </Menu>
    </>
  );
};
